import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
  Eye,
  EyeOff,
  User as UserIcon,
  Mail,
  Lock,
  ArrowRight,
  CheckCircle2,
} from 'lucide-react';
import Alert from '../common/Alert';
import GoogleButton from './GoogleButton';

const RegisterForm = () => {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!acceptTerms) {
      setError('Please accept the Terms of Service and Privacy Policy');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    if (!validateForm()) {
      setLoading(false);
      return;
    }
    const result = await register(
      formData.username,
      formData.email,
      formData.password,
      acceptTerms
    );
    if (result.success) navigate('/');
    else setError(result.message);
    setLoading(false);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-10 items-stretch max-w-6xl mx-auto -my-4 lg:-my-8">
      <div className="flex items-center justify-center py-4 lg:py-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink-500 hover:text-ink-700"
            >
              ← Back to home
            </Link>
            <h1 className="mt-6 text-3xl font-bold tracking-tight text-ink-900">
              Create your account
            </h1>
            <p className="mt-2 text-sm text-ink-500">
              Start shortening, deep-linking and tracking in minutes.
            </p>
          </div>

          {error && <Alert type="error" message={error} className="mb-5" />}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-ink-700">
                Username
              </label>
              <div className="relative">
                <UserIcon className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type="text"
                  name="username"
                  value={formData.username}
                  onChange={handleChange}
                  required
                  placeholder="yourname"
                  className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-ink-700">
                Email
              </label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder="you@company.com"
                  className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-3.5 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-700">
                  Password
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-10 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="block text-sm font-medium text-ink-700">
                  Confirm
                </label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-400" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full rounded-lg border border-ink-200 bg-white pl-10 pr-10 py-2.5 text-sm text-ink-900 placeholder-ink-400 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-7 w-7 items-center justify-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-ink-100"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>
            </div>

            <label className="flex items-start gap-2.5 text-xs text-ink-600 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={acceptTerms}
                onChange={(e) => setAcceptTerms(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-ink-300 text-brand-600 focus:ring-brand-500"
              />
              <span>
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="font-medium text-brand-600 hover:text-brand-700 underline">Terms of Service</Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="font-medium text-brand-600 hover:text-brand-700 underline">Privacy Policy</Link>.
              </span>
            </label>

            <button
              type="submit"
              disabled={loading}
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-ink-900 px-4 py-2.5 text-sm font-semibold text-white shadow-soft hover:bg-ink-800 hover:shadow-card transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account…' : 'Create account'}
              {!loading && <ArrowRight className="h-4 w-4" />}
            </button>
          </form>

          <GoogleButton label="Sign up with Google" />

          <p className="mt-6 text-center text-sm text-ink-500">
            Already have an account?{' '}
            <Link
              to="/login"
              className="font-semibold text-brand-600 hover:text-brand-700"
            >
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block">
        <div className="relative h-full overflow-hidden rounded-3xl border border-ink-200 bg-gradient-to-br from-brand-600 via-violet-600 to-fuchsia-600 text-white shadow-pop">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="relative h-full flex flex-col p-10">
            <h2 className="text-3xl font-bold tracking-tight leading-tight">
              Everything you get,
              <br /> on day one.
            </h2>
            <ul className="mt-8 space-y-3">
              {[
                'Unlimited short links during beta',
                'Built-in QR code generation',
                'iOS Universal Links & Android App Links',
                'Real-time analytics & geo breakdowns',
                'REST API with personal access keys',
                'Link-in-bio profile pages',
              ].map((f) => (
                <li key={f} className="flex items-start gap-3 text-white/90">
                  <CheckCircle2 className="h-5 w-5 text-white shrink-0 mt-0.5" />
                  <span className="text-sm">{f}</span>
                </li>
              ))}
            </ul>
            <div className="mt-auto rounded-2xl bg-white/10 border border-white/15 p-5 backdrop-blur">
              <p className="text-sm font-medium">"Replaced FDL in an afternoon."</p>
              <p className="mt-1 text-xs text-white/70">
                — Team using Dynalink since beta
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegisterForm;
